import { useRef, useState } from 'react';
import { useMotionValue, useTransform } from 'framer-motion';

const MIN_SCALE = 0.5;
const MAX_SCALE = 1.0;
// Phones see less than 10% of the 4500px board at scale 1 — start them zoomed
// out so they get more path context around the current step. Desktop keeps
// native scale; tablets land in between.
const SMALL_SCREEN_BREAKPOINT = 768;
const initialBoardScale = () => {
  if (typeof window === 'undefined') return 1;
  return window.innerWidth < SMALL_SCREEN_BREAKPOINT ? 0.65 : 1;
};

// Encapsulates the click-and-drag scrolling on the game board, the pinch-to-
// zoom-out gesture on the board itself, and the three parallax motion values.
// Returning motion values (NOT React state) for parallax is what keeps drag
// smooth — scroll updates do not trigger an App re-render; only the 3 parallax
// layers' transforms tick. Scale lives in React state because GameScreen needs
// to re-render the board's transform and its layout container's dimensions.
export function useDragScroll() {
  const containerRef = useRef(null);
  const dragState = useRef({
    isPreventClick: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [scale, setScale] = useState(initialBoardScale);
  const scaleRef = useRef(scale);
  // Pinch gesture bookkeeping. Anchor coords are in *unscaled* board space so
  // the same board point stays pinned under the user's fingers as scale moves.
  // rectLeft/rectTop are cached at gesture start — getBoundingClientRect()
  // forces a synchronous layout, so we read it once per pinch instead of
  // every touchmove.
  const pinchState = useRef({
    active: false,
    initialDist: 0,
    initialScale: 1,
    anchorBoardX: 0,
    anchorBoardY: 0,
    rectLeft: 0,
    rectTop: 0,
  });
  // rAF coalesces multiple touchmove events into one setScale per frame — on
  // some Android browsers the touch stream fires faster than the paint pipeline
  // can keep up and we'd otherwise queue up redundant React renders.
  const pinchRaf = useRef(null);
  const pendingTouches = useRef(null);

  const scrollX = useMotionValue(0);
  const scrollY = useMotionValue(0);
  const parallaxSunX = useTransform(scrollX, (v) => -v * 0.05);
  const parallaxSunY = useTransform(scrollY, (v) => -v * 0.05);
  const parallaxCloudsX = useTransform(scrollX, (v) => -v * 0.1);
  const parallaxCloudsY = useTransform(scrollY, (v) => -v * 0.1);
  const parallaxHillsX = useTransform(scrollX, (v) => -v * 0.2);
  const parallaxHillsY = useTransform(scrollY, (v) => -v * 0.2);

  const onScroll = (e) => {
    scrollX.set(e.target.scrollLeft);
    scrollY.set(e.target.scrollTop);
  };

  const beginDrag = (pageX, pageY) => {
    setHasInteracted(true);
    setIsDragging(true);
    dragState.current = {
      isPreventClick: false,
      startX: pageX - containerRef.current.offsetLeft,
      startY: pageY - containerRef.current.offsetTop,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop,
    };
  };

  const moveDrag = (pageX, pageY) => {
    if (!isDragging || !containerRef.current) return;
    const x = pageX - containerRef.current.offsetLeft;
    const y = pageY - containerRef.current.offsetTop;
    const walkX = (x - dragState.current.startX) * 1.5;
    const walkY = (y - dragState.current.startY) * 1.5;
    if (Math.abs(walkX) > 5 || Math.abs(walkY) > 5) {
      dragState.current.isPreventClick = true;
    }
    containerRef.current.scrollLeft = dragState.current.scrollLeft - walkX;
    containerRef.current.scrollTop = dragState.current.scrollTop - walkY;
  };

  const endDrag = () => setIsDragging(false);

  // Pinch handling. The board itself is transform-scaled (not the viewport),
  // so guests can zoom out to see how avatars walk the full path without the
  // browser scaling the surrounding UI.
  const touchDist = (touches) => {
    const dx = touches[0].pageX - touches[1].pageX;
    const dy = touches[0].pageY - touches[1].pageY;
    return Math.hypot(dx, dy);
  };

  const beginPinch = (touches) => {
    endDrag();
    const c = containerRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const midClientX = (touches[0].clientX + touches[1].clientX) / 2;
    const midClientY = (touches[0].clientY + touches[1].clientY) / 2;
    // Convert pinch midpoint to unscaled board coords so we can pin it.
    const boardX = (midClientX - rect.left + c.scrollLeft) / scaleRef.current;
    const boardY = (midClientY - rect.top + c.scrollTop) / scaleRef.current;
    pinchState.current = {
      active: true,
      initialDist: touchDist(touches),
      initialScale: scaleRef.current,
      anchorBoardX: boardX,
      anchorBoardY: boardY,
      rectLeft: rect.left,
      rectTop: rect.top,
    };
    dragState.current.isPreventClick = true;
  };

  const applyPinch = () => {
    pinchRaf.current = null;
    const touches = pendingTouches.current;
    const c = containerRef.current;
    if (!touches || !c || !pinchState.current.active) return;
    const dist = touchDist(touches);
    if (!pinchState.current.initialDist) return;
    let next = pinchState.current.initialScale * (dist / pinchState.current.initialDist);
    next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
    scaleRef.current = next;
    setScale(next);
    // Pin the original board point under the (current) pinch midpoint, using
    // the cached container offset (cheaper than re-reading getBoundingClientRect).
    const midClientX = (touches[0].clientX + touches[1].clientX) / 2;
    const midClientY = (touches[0].clientY + touches[1].clientY) / 2;
    c.scrollLeft = pinchState.current.anchorBoardX * next - (midClientX - pinchState.current.rectLeft);
    c.scrollTop = pinchState.current.anchorBoardY * next - (midClientY - pinchState.current.rectTop);
  };

  const movePinch = (touches) => {
    if (!pinchState.current.active) return;
    // Snapshot the touch points (TouchList is live; rAF callback fires later).
    pendingTouches.current = [
      { clientX: touches[0].clientX, clientY: touches[0].clientY, pageX: touches[0].pageX, pageY: touches[0].pageY },
      { clientX: touches[1].clientX, clientY: touches[1].clientY, pageX: touches[1].pageX, pageY: touches[1].pageY },
    ];
    if (pinchRaf.current == null) {
      pinchRaf.current = requestAnimationFrame(applyPinch);
    }
  };

  const onMouseDown = (e) => beginDrag(e.pageX, e.pageY);
  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    moveDrag(e.pageX, e.pageY);
  };
  const onTouchStart = (e) => {
    if (e.touches.length >= 2) {
      beginPinch(e.touches);
      return;
    }
    beginDrag(e.touches[0].pageX, e.touches[0].pageY);
  };
  const onTouchMove = (e) => {
    if (pinchState.current.active && e.touches.length >= 2) {
      movePinch(e.touches);
      return;
    }
    if (e.touches.length > 1) return;
    moveDrag(e.touches[0].pageX, e.touches[0].pageY);
  };
  const onTouchEnd = (e) => {
    if (pinchState.current.active && e.touches.length < 2) {
      pinchState.current.active = false;
      if (pinchRaf.current != null) {
        cancelAnimationFrame(pinchRaf.current);
        pinchRaf.current = null;
      }
      pendingTouches.current = null;
    }
    endDrag();
  };

  const containerProps = {
    ref: containerRef,
    onScroll,
    onMouseDown,
    onMouseLeave: endDrag,
    onMouseUp: endDrag,
    onMouseMove,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
    // `touch-action: none` lets us take over pan + pinch on the board so
    // pinching zooms the inner game world instead of the browser viewport.
    style: { touchAction: 'none' },
  };

  return {
    containerRef,
    containerProps,
    dragState,
    hasInteracted,
    scale,
    scaleRef,
    parallax: {
      sun: { x: parallaxSunX, y: parallaxSunY },
      clouds: { x: parallaxCloudsX, y: parallaxCloudsY },
      hills: { x: parallaxHillsX, y: parallaxHillsY },
    },
  };
}
